import AxiosInstance from '../../helpers/axios-instance/axios-instance';
import { appConfig } from '../../helpers/app-config/app-config';
import type {
  Component,
  DataEntryComponent,
  Page,
  PageTemplate,
  PageTemplateUpdateResponse,
  PageTemplateWritePayload,
  PageWritePayload,
} from './pages-client.type';

const axiosInstance = AxiosInstance();
const pageBaseURL = appConfig.VITE_APP_API_URL + '/api/pages/';
const componentBaseURL = appConfig.VITE_APP_API_URL + '/api/components/';
const templateBaseURL = appConfig.VITE_APP_API_URL + '/api/page-templates/';
const dataEntryComponentBaseURL = appConfig.VITE_APP_API_URL + '/api/data-entry-components/';

/**
 * Fetches pages based on provided filters
 * @param organizationId - The ID of the organization to filter pages by
 * @param portfolioId - The ID of the portfolio to filter pages by
 * @param type - The type of pages to filter by
 * @param moduleType - The module type to filter pages by
 * @returns Promise containing the fetched pages data
 * @throws Will reject with error object if the API request fails
 */
function fetchPages(organizationId: string, portfolioId: string, type: string, moduleType: string): Promise<Page[]> {
  let apiURL = pageBaseURL + 'get_pages/?';

  if (organizationId) {
    apiURL += `organization=${organizationId}&`;
  }
  if (portfolioId) {
    apiURL += `portfolio=${portfolioId}&`;
  }
  if (type) {
    apiURL += `type=${type}&`;
  }
  if (moduleType) {
    apiURL += `module_type=${moduleType}`;
  }

  return axiosInstance
    .get(apiURL)
    .then((response) => response.data)
    .catch((error) => Promise.reject({ error: error.response.data }));
}

/**
 * Creates a new page
 * @param payload - The page data to create
 * @returns Promise containing the created page data
 * @throws Will reject with error object if the API request fails
 */
function createPage(payload: PageWritePayload): Promise<Page> {
  const apiURL = pageBaseURL + 'create_page/';
  return axiosInstance
    .post(apiURL, payload)
    .then((response) => response.data)
    .catch((error) => Promise.reject({ error: error.response.data }));
}

/**
 * Updates an existing page
 * @param id - The ID of the page to update
 * @param payload - The updated page data
 * @returns Promise containing the updated page data
 * @throws Will reject with error object if the API request fails
 */
function updatePage(id: string, payload: Page): Promise<Page> {
  const apiURL = pageBaseURL + id + '/edit_page/';
  return axiosInstance
    .put(apiURL, payload)
    .then((response) => response.data)
    .catch((error) => Promise.reject({ error: error.response.data }));
}

/**
 * Deletes a page
 * @param id - The ID of the page to delete
 * @returns Promise containing the response data from the deletion
 * @throws Will reject with error object if the API request fails
 */
function deletePage(id: string) {
  const apiURL = pageBaseURL + id + '/delete_page/';

  return axiosInstance
    .delete(apiURL)
    .then((response) => response.data)
    .catch((error) => Promise.reject({ error: error.response.data }));
}

/**
 * Creates a new component
 * @param payload - The component data to create
 * @returns Promise containing the created component data
 * @throws Will reject with error object if the API request fails
 */
function createComponent(payload: Component): Promise<Component> {
  const apiURL = componentBaseURL + 'create_component/';
  return axiosInstance
    .post(apiURL, payload)
    .then((response) => response.data)
    .catch((error) => Promise.reject({ error: error.response.data }));
}

/**
 * Updates an existing component
 * @param component_id - The ID of the component to update
 * @param payload - The updated component data
 * @returns Promise containing the updated component data
 * @throws Will reject with error object if the API request fails
 */
function updateComponent(component_id: string, payload: Component): Promise<Component> {
  const apiURL = componentBaseURL + component_id + '/edit/';
  return axiosInstance
    .put(apiURL, payload)
    .then((response) => response.data)
    .catch((error) => Promise.reject({ error: error.response.data }));
}

/**
 * Deletes a component
 * @param component_id - The ID of the component to delete
 * @returns Promise containing the response data from the deletion
 * @throws Will reject with error object if the API request fails
 */
function deleteComponent(component_id: string): Promise<void> {
  const apiURL = componentBaseURL + component_id + '/';
  return axiosInstance
    .delete(apiURL)
    .then((response) => response.data)
    .catch((error) => Promise.reject({ error: error.response.data }));
}

/**
 * Creates a new page template
 * @param {string} page_id - The ID of the page to create the template for
 * @param {PageTemplateWritePayload} payload - The page template data to create
 
 * @returns Promise containing the created page template data
 * @throws Will reject with error object if the API request fails
 */
function createPageTemplate(page_id: string, payload: PageTemplateWritePayload): Promise<PageTemplate> {
  const apiURL = pageBaseURL + page_id + '/create_page_template/';
  return axiosInstance
    .post(apiURL, payload)
    .then((response) => response.data)
    .catch((error) => Promise.reject({ error: error.response.data }));
}

/**
 * Fetches page templates based on provided filters
 * @param {string} organizationId - The ID of the organization to filter templates by
 * @param {string | undefined} portfolioId - The ID of the portfolio to filter templates by
 * @returns Promise containing the fetched templates data
 * @throws Will reject with error object if the API request fails
 */
function listPageTemplates(organizationId: string, portfolioId?: string): Promise<PageTemplate[]> {
  let apiURL = templateBaseURL + `?organization=${organizationId}`;

  if (portfolioId) {
    apiURL += `&portfolio=${portfolioId}`;
  }
  return axiosInstance
    .get(apiURL)
    .then((response) => response.data)
    .catch((error) => Promise.reject({ error: error.response.data }));
}

/**
 * Deletes a page template
 * @param id - The ID of the page template to delete
 * @returns Promise containing the response data from the deletion
 * @throws Will reject with error object if the API request fails
 */
function deletePageTemplate(id: string): Promise<void> {
  const apiURL = templateBaseURL + id + '/';
  return axiosInstance
    .delete(apiURL)
    .then((response) => response.data)
    .catch((error) => Promise.reject({ error: error.response.data }));
}

/**
 * Updates an existing page template. When the payload includes `config`, the
 * backend cascades the new config to all child pages (pages created from this
 * template) and returns a `cascade` summary on the response.
 * @param id - The ID of the page template to update
 * @param {PageTemplateWritePayload} payload - The updated page template data
 * @returns Promise containing the updated page template plus optional cascade summary
 * @throws Will reject with error object if the API request fails
 */
function updatePageTemplate(id: string, payload: PageTemplateWritePayload): Promise<PageTemplateUpdateResponse> {
  const apiURL = templateBaseURL + id + '/';
  return axiosInstance
    .put(apiURL, payload)
    .then((response) => response.data)
    .catch((error) => Promise.reject({ error: error.response.data }));
}

/**
 * Creates a new data entry component
 * @param {DataEntryComponent} payload - The data entry component data to create
 * @returns {Promise<DataEntryComponent>} Promise containing the created data entry component data
 * @throws Will reject with error object if the API request fails
 */
function createDataEntryComponent(payload: DataEntryComponent): Promise<DataEntryComponent> {
  const apiURL = dataEntryComponentBaseURL;
  return axiosInstance
    .post(apiURL, payload)
    .then((response) => response.data)
    .catch((error) => Promise.reject({ error: error.response.data }));
}

/**
 * Updates an existing data entry component
 * @param {string} id - The ID of the data entry component to update
 * @param {DataEntryComponent} payload - The updated data entry component data
 * @returns {Promise<DataEntryComponent>} Promise containing the updated data entry component data
 * @throws Will reject with error object if the API request fails
 */
function updateDataEntryComponent(id: string, payload: DataEntryComponent): Promise<DataEntryComponent> {
  const apiURL = dataEntryComponentBaseURL + id + '/';
  return axiosInstance
    .put(apiURL, payload)
    .then((response) => response.data)
    .catch((error) => Promise.reject({ error: error.response.data }));
}

/**
 * Deletes a data entry component
 * @param {string} id - The ID of the data entry component to delete
 * @returns {Promise<void>} Promise containing the response data from the deletion
 * @throws Will reject with error object if the API request fails
 */
function deleteDataEntryComponent(id: string): Promise<void> {
  const apiURL = dataEntryComponentBaseURL + id + '/';
  return axiosInstance
    .delete(apiURL)
    .then((response) => response.data)
    .catch((error) => Promise.reject({ error: error.response.data }));
}

export {
  createComponent,
  createDataEntryComponent,
  createPage,
  createPageTemplate,
  deleteComponent,
  deleteDataEntryComponent,
  deletePage,
  deletePageTemplate,
  fetchPages,
  listPageTemplates,
  updateComponent,
  updateDataEntryComponent,
  updatePage,
  updatePageTemplate,
};
